import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import * as RToast from '@radix-ui/react-toast';
import { AlertTriangle, CheckCircle2, Info, Loader2, X, XCircle } from 'lucide-react';
import { cn } from './cn.js';
import { Button } from './Button.js';
import type { Tone } from './Badge.js';

// ---------------------------------------------------------------------------
// Skeletons
// ---------------------------------------------------------------------------

/**
 * Skeletons rather than spinners for content that has a known shape. A layout
 * that appears and then fills reads as fast; a spinner that is replaced by a
 * layout reads as a page load.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn(
        'relative overflow-hidden rounded-sm bg-surface-sunken',
        // A travelling sheen, clipped to the block.
        'after:absolute after:inset-0 after:-translate-x-full after:bg-gradient-to-r',
        'after:from-transparent after:via-black/[0.045] after:to-transparent',
        'after:animate-[shimmer_1.6s_infinite]',
        'dark:after:via-white/[0.06]',
        className,
      )}
    />
  );
}

export function SkeletonTable({ rows = 8, cols = 6 }: { rows?: number; cols?: number }) {
  return (
    <div role="status" aria-label="Loading" className="divide-y divide-line-subtle">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-3 px-3 py-2.5">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton
              key={c}
              className={cn('h-3.5', c === 1 ? 'flex-[3]' : 'flex-1')}
              // Slight width variation keeps it from looking like a grid.
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonMetrics({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-lg border border-line bg-surface p-3.5">
          <Skeleton className="h-2.5 w-20" />
          <Skeleton className="mt-2.5 h-6 w-16" />
          <Skeleton className="mt-2.5 h-2.5 w-28" />
        </div>
      ))}
    </div>
  );
}

export function Spinner({ label, className }: { label?: string; className?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn('flex items-center justify-center gap-2 py-10', className)}
    >
      <Loader2 aria-hidden className="h-4 w-4 animate-spin text-ink-tertiary" />
      {label && <span className="text-xs text-ink-secondary">{label}…</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty and error states
// ---------------------------------------------------------------------------

export function EmptyState({
  icon,
  title,
  hint,
  action,
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}>
      {icon && (
        <div
          aria-hidden
          className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg border border-line bg-surface-sunken text-ink-tertiary [&>svg]:h-4.5 [&>svg]:w-4.5"
        >
          {icon}
        </div>
      )}
      <p className="text-sm font-medium text-ink">{title}</p>
      {hint && <p className="mt-1.5 max-w-sm text-xs leading-relaxed text-ink-secondary">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

const CALLOUT_TONE: Record<Tone, { wrap: string; icon: ReactNode }> = {
  neutral: { wrap: 'border-line bg-surface-sunken text-ink-secondary', icon: <Info /> },
  brand: { wrap: 'border-brand-line/60 bg-brand-tint text-brand', icon: <Info /> },
  accent: { wrap: 'border-accent-line/60 bg-accent-tint text-accent', icon: <AlertTriangle /> },
  ok: { wrap: 'border-ok-line/60 bg-ok-tint text-ok', icon: <CheckCircle2 /> },
  warn: { wrap: 'border-warn-line/60 bg-warn-tint text-warn', icon: <AlertTriangle /> },
  danger: { wrap: 'border-danger-line/60 bg-danger-tint text-danger', icon: <XCircle /> },
  info: { wrap: 'border-info-line/60 bg-info-tint text-info', icon: <Info /> },
};

export function Callout({
  tone = 'neutral',
  title,
  children,
  icon,
  className,
}: {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  const t = CALLOUT_TONE[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : undefined}
      className={cn('flex gap-2.5 rounded-md border px-3 py-2.5 text-xs', t.wrap, className)}
    >
      <span aria-hidden className="mt-px shrink-0 [&>svg]:h-3.5 [&>svg]:w-3.5">
        {icon ?? t.icon}
      </span>
      <div className="min-w-0 leading-relaxed">
        {title && <p className="font-semibold">{title}</p>}
        {children}
      </div>
    </div>
  );
}

/** Renders any thrown value without leaking a stack into the UI. */
export function ErrorState({ error, className }: { error: unknown; className?: string }) {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : 'Something failed';
  const fields =
    error && typeof error === 'object' && 'fields' in error
      ? (error as { fields?: { key: string; message: string }[] }).fields
      : undefined;

  return (
    <Callout tone="danger" className={className}>
      {message}
      {fields && fields.length > 0 && (
        <ul className="mt-1.5 space-y-0.5">
          {fields.map((f) => (
            <li key={f.key}>
              <span className="font-medium">{f.key}</span>: {f.message}
            </li>
          ))}
        </ul>
      )}
    </Callout>
  );
}

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------

interface ToastItem {
  id: number;
  tone: Tone;
  title: string;
  description?: string;
  /** One button beside the text — "Show", "Undo". Closes the toast. */
  action?: { label: string; onSelect: () => void };
  /** How long it stays, in ms. Defaults to the provider's. */
  duration?: number;
}

interface ToastApi {
  toast: (t: Omit<ToastItem, 'id'>) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
}

const ToastCtx = createContext<ToastApi>({
  toast: () => undefined,
  success: () => undefined,
  error: () => undefined,
});

export function useToast(): ToastApi {
  return useContext(ToastCtx);
}

/**
 * Toasts confirm that a mutation landed. Without them every action in a
 * dense tool feels like it might not have worked, which is most of why the
 * first pass felt unresponsive.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const toast = useCallback((t: Omit<ToastItem, 'id'>) => {
    setItems((prev) => [...prev.slice(-3), { ...t, id: Date.now() + Math.random() }]);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      toast,
      success: (title, description) => toast({ tone: 'ok', title, description }),
      error: (title, description) => toast({ tone: 'danger', title, description }),
    }),
    [toast],
  );

  const ICON: Record<Tone, ReactNode> = {
    neutral: <Info />,
    brand: <Info />,
    accent: <AlertTriangle />,
    info: <Info />,
    ok: <CheckCircle2 />,
    warn: <AlertTriangle />,
    danger: <XCircle />,
  };

  const ACCENT: Record<Tone, string> = {
    neutral: 'text-ink-tertiary',
    brand: 'text-brand',
    accent: 'text-accent',
    info: 'text-info',
    ok: 'text-ok',
    warn: 'text-warn',
    danger: 'text-danger',
  };

  return (
    <ToastCtx.Provider value={api}>
      <RToast.Provider duration={4200} swipeDirection="right">
        {children}
        {items.map((t) => (
          <RToast.Root
            key={t.id}
            duration={t.duration}
            onOpenChange={(open) => {
              if (!open) setItems((prev) => prev.filter((x) => x.id !== t.id));
            }}
            className={cn(
              'flex items-start gap-2.5 rounded-lg border border-line bg-surface-raised p-3 shadow-lg',
              'data-[state=open]:animate-[slide-in-right_var(--dur)_var(--ease-out)]',
              'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
              'data-[swipe=cancel]:translate-x-0',
            )}
          >
            <span aria-hidden className={cn('mt-px shrink-0 [&>svg]:h-4 [&>svg]:w-4', ACCENT[t.tone])}>
              {ICON[t.tone]}
            </span>
            <div className="min-w-0 flex-1">
              <RToast.Title className="text-xs font-semibold text-ink">{t.title}</RToast.Title>
              {t.description && (
                <RToast.Description className="mt-0.5 text-2xs leading-snug text-ink-secondary">
                  {t.description}
                </RToast.Description>
              )}
            </div>
            {t.action && (
              // altText is what a screen reader is told to do instead, since
              // a toast can vanish before the button is reached.
              <RToast.Action asChild altText={t.action.label}>
                <Button variant="ghost" size="xs" onClick={t.action.onSelect} className="shrink-0 text-brand">
                  {t.action.label}
                </Button>
              </RToast.Action>
            )}
            <RToast.Close asChild>
              <Button variant="ghost" size="xs" iconOnly aria-label="Dismiss">
                <X aria-hidden className="h-3 w-3" />
              </Button>
            </RToast.Close>
          </RToast.Root>
        ))}
        <RToast.Viewport className="fixed bottom-4 right-4 z-[60] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2 outline-none" />
      </RToast.Provider>
    </ToastCtx.Provider>
  );
}
